import React from 'react';
import { FiCheck, FiHeadphones, FiVolume2 } from 'react-icons/fi';
import { useI18n } from '../i18n/I18nProvider';

export const MeaningChoices = ({ choices = [], selected = new Set(), feedback, onToggle, multiple = false }) => (
  <div className="grid gap-3 sm:grid-cols-2 mt-6">
    {choices.map((choice) => {
      const isSelected = selected.has(choice.key);
      const expected = feedback && choice.correct;
      const selectedWrong = feedback && isSelected && !choice.correct;
      return (
        <button
          type="button"
          key={choice.key}
          disabled={Boolean(feedback)}
          onClick={() => onToggle(choice.key)}
          aria-pressed={isSelected}
          className={`min-h-[68px] border-2 rounded-lg px-4 py-3 text-left font-medium ${
            expected ? 'border-emerald-500 bg-emerald-50'
              : selectedWrong ? 'border-rose-500 bg-rose-50'
                : isSelected ? 'border-indigo-500 bg-indigo-50'
                  : 'border-gray-200 hover:border-indigo-400'
          }`}
          data-i18n-skip
        >
          <span className="inline-flex items-start gap-2">
            <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center border ${multiple ? '' : 'rounded-full'} ${isSelected ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-gray-300'}`}>
              {isSelected && <FiCheck size={14} />}
            </span>
            {choice.label}
          </span>
        </button>
      );
    })}
  </div>
);

const feedbackPartClass = (type) => (
  type === 'match'
    ? 'text-gray-900'
    : type === 'missing'
      ? 'text-rose-600 underline'
      : 'text-amber-600 line-through'
);

const DictationQuestion = ({
  word,
  choices,
  answer,
  selected = new Set(),
  feedback,
  onAnswerChange,
  onToggleChoice,
  onSubmit,
  onAdvance,
  onReplay,
  inputRef,
  multiple = false,
  showIntro = true,
  showReplay = true,
  inputId = 'dictation-spelling',
}) => {
  const { t } = useI18n();
  const selectedSet = selected instanceof Set ? selected : new Set(selected ? [selected] : []);

  return (
    <div className="p-6 sm:p-10 text-center">
      {showIntro && (
        <>
          <FiHeadphones className="mx-auto text-indigo-600" size={34} />
          <div className="mt-4 text-sm font-semibold uppercase text-gray-500">{t('Listen, spell, and identify the meaning')}</div>
        </>
      )}
      {showReplay && (
        <button
          type="button"
          onClick={onReplay}
          className="mt-5 inline-flex items-center gap-2 rounded border border-indigo-300 bg-white px-4 py-2 font-semibold text-indigo-700 hover:bg-indigo-50"
          title={t('Replay word')}
        >
          <FiVolume2 />{t('Replay word')}
        </button>
      )}
      <form onSubmit={onSubmit} className="mt-5">
        <div className="text-left text-sm font-semibold text-gray-700">{t('1. Type the word you hear')}</div>
        <input
          ref={inputRef}
          id={inputId}
          value={answer}
          onChange={(event) => onAnswerChange(event.target.value)}
          disabled={Boolean(feedback)}
          autoComplete="off"
          spellCheck="false"
          className="w-full max-w-lg text-center text-2xl border-b-2 border-gray-300 px-3 py-3 focus:border-indigo-600 focus:outline-none bg-transparent"
          placeholder={t('Type what you hear')}
          data-i18n-skip
        />
        <div className="mt-7 text-left text-sm font-semibold text-gray-700">
          {t(multiple ? '2. Choose every matching meaning' : '2. Choose the matching meaning')}
        </div>
        <MeaningChoices
          choices={choices}
          selected={selectedSet}
          feedback={feedback}
          onToggle={onToggleChoice}
          multiple={multiple}
        />
        {!feedback && <button disabled={!answer.trim() || !selectedSet.size} className="mt-6 px-6 py-3 bg-indigo-600 text-white rounded-md font-semibold disabled:opacity-50">{t('Check answer')}</button>}
      </form>
      {feedback?.correct && <div className="mt-6 text-emerald-700 font-semibold inline-flex items-center gap-2"><FiCheck /> {t('Correct')}</div>}
      {feedback && !feedback.correct && (
        <div className="mt-7 border border-rose-200 bg-rose-50 rounded-lg p-5 text-left">
          <div className="font-semibold text-rose-800">{t('This word will return in the next round.')}</div>
          {feedback.parts && (
            <div className="mt-3 font-mono text-2xl tracking-wide" data-i18n-skip>
              {feedback.parts.map((part, partIndex) => <span key={`${partIndex}-${part.value}`} className={feedbackPartClass(part.type)}>{part.value}</span>)}
            </div>
          )}
          <div className="mt-2 text-sm text-gray-700">
            {t('Spelling')}: {feedback.spellingCorrect ? t('Correct') : t('Incorrect')}
            {' / '}
            {t('Meaning')}: {feedback.meaningCorrect ? t('Correct') : t('Incorrect')}
          </div>
          {feedback.expected && <div className="mt-2 text-sm text-gray-600">{t('Correct spelling:')} <strong data-i18n-skip>{feedback.expected}</strong></div>}
          <button type="button" onClick={onAdvance} className="mt-4 px-5 py-2 bg-gray-900 text-white rounded-md">{t('Continue')}</button>
        </div>
      )}
    </div>
  );
};

export default DictationQuestion;
